import { useState } from "react";
import { Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { MessageFeedback } from "@/types/brain/brain";

interface MessageActionsProps {
  content: string;
  messageId: string;
  feedback?: MessageFeedback | null;
  onFeedback?: (messageId: string, rating: "positive" | "negative") => void;
  isMobile?: boolean;
}

const MessageActions = ({
  content,
  messageId,
  feedback,
  onFeedback,
  isMobile,
}: MessageActionsProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — try selecting the text manually.");
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-1 mt-1",
        !isMobile && "opacity-0 group-hover:opacity-100 transition-opacity duration-150",
      )}
    >
      {/* Copy */}
      <button
        onClick={handleCopy}
        className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
        title="Copy message"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Thumbs Up */}
      <button
        onClick={() => onFeedback?.(messageId, "positive")}
        className={cn(
          "p-1 rounded hover:bg-gray-200 transition-colors",
          feedback?.rating === "positive"
            ? "text-primary"
            : "text-gray-400 hover:text-gray-600",
        )}
        title="Helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>

      {/* Thumbs Down */}
      <button
        onClick={() => onFeedback?.(messageId, "negative")}
        className={cn(
          "p-1 rounded hover:bg-gray-200 transition-colors",
          feedback?.rating === "negative"
            ? "text-red-400"
            : "text-gray-400 hover:text-gray-600",
        )}
        title="Not helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default MessageActions;
